import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Building2, PoundSterling, ArrowRight, Calculator, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { ThemeToggle } from "@/components/ThemeProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvestorLogin() {
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [investorToken, setInvestorToken] = useState(null);
  
  // Session creation form
  const [projectName, setProjectName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const storedToken = sessionStorage.getItem("investor_token");
    if (storedToken) {
      setInvestorToken(storedToken);
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = async () => {
    if (!inviteCode.trim()) {
      toast.error("Please enter your investor code");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/investor/login`, {
        invite_code: inviteCode.trim()
      });
      
      if (response.data.success) {
        sessionStorage.setItem("investor_token", response.data.investor_token);
        setInvestorToken(response.data.investor_token);
        setIsLoggedIn(true);
        toast.success("Welcome, Investor");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid investor code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSession = async () => {
    if (!projectName.trim() || !postcode.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/investor/session`, {
        project_name: projectName.trim(),
        postcode: postcode.trim().toUpperCase(),
        investor_token: investorToken
      });
      
      const sessionId = response.data.session_id;
      
      // Store session info
      sessionStorage.setItem(`investor_session_${sessionId}`, JSON.stringify({
        project_name: projectName.trim(),
        postcode: postcode.trim().toUpperCase(),
      }));
      
      navigate(`/investor/express/${sessionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create session");
    } finally {
      setIsCreating(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("investor_token");
    setInvestorToken(null);
    setIsLoggedIn(false);
    setInviteCode("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-white/10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-sm font-bold text-white">Investor Lens</span>
          </div>
          <div className="flex items-center gap-2">
            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className="text-xs text-white/50 hover:text-white/70 transition-colors"
              >
                Logout
              </button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          {!isLoggedIn ? (
            /* Login Form */
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-7 h-7 text-blue-400" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">Property Investor Portal</h1>
                <p className="text-sm text-white/50">
                  Analyse investment potential with ROI-focused insights
                </p>
              </div>

              {/* Features Preview */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                  <PoundSterling className="w-4 h-4 text-blue-400 mb-2" />
                  <p className="text-xs text-white/70">Yield Analysis</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                  <BarChart3 className="w-4 h-4 text-blue-400 mb-2" />
                  <p className="text-xs text-white/70">ROI Calculator</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                  <Building2 className="w-4 h-4 text-blue-400 mb-2" />
                  <p className="text-xs text-white/70">Portfolio View</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                  <TrendingUp className="w-4 h-4 text-blue-400 mb-2" />
                  <p className="text-xs text-white/70">Cash-on-Cash</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2 block">
                    Investor Code
                  </label>
                  <Input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    placeholder="Enter your investor code"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    data-testid="investor-code-input"
                  />
                </div>
                <Button
                  onClick={handleLogin}
                  disabled={isLoading || !inviteCode.trim()}
                  className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-medium"
                  data-testid="investor-login-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Access Portal
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>

              <p className="text-[10px] text-white/30 text-center mt-4">
                Investor access is by invitation only
              </p>
            </div>
          ) : (
            /* Session Creation Form */
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-7 h-7 text-blue-400" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">New Investment Analysis</h1>
                <p className="text-sm text-white/50">
                  Start a new property investment assessment
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2 block">
                    Project Name
                  </label>
                  <Input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g. 42 Oak Street BTL"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    data-testid="investor-project-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2 block">
                    Property Postcode
                  </label>
                  <Input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && handleCreateSession()}
                    placeholder="e.g. SW1A 1AA"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 uppercase"
                    data-testid="investor-postcode-input"
                  />
                </div>
                <Button
                  onClick={handleCreateSession}
                  disabled={isCreating || !projectName.trim() || !postcode.trim()}
                  className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-medium"
                  data-testid="investor-start-button"
                >
                  {isCreating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Start Analysis
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 border-t border-white/5">
        <p className="text-[10px] text-white/30 text-center">
          Investment analysis powered by HiPPO Intelligence
        </p>
      </footer>
    </div>
  );
}
